import React from 'react';
import { Text, cn } from '@billda/design-system';
import { UserAvatar } from '../../atoms/UserAvatar';
import type { StoredAccount } from '@billda/shared-ui';

interface AccountListItemProps {
  account: StoredAccount;
  isCurrent: boolean;
  isExpired: boolean;
  onClick: (email: string) => void;
  disabled?: boolean;
}

/**
 * Molecule: Single account list item (DS fork of shared-ui AccountListItem)
 * @example
 * ```tsx
 * <AccountListItem
 *   account={storedAccount}
 *   isCurrent={false}
 *   isExpired={false}
 *   onClick={(email) => handleSwitch(email)}
 * />
 * ```
 */
export const AccountListItem: React.FC<AccountListItemProps> = ({
  account,
  isCurrent,
  isExpired,
  onClick,
  disabled = false,
}) => {
  return (
    <button
      type="button"
      onClick={() => onClick(account.email)}
      disabled={disabled}
      data-testid={`account-item-${account.email}`}
      className={cn(
        'flex w-full items-center gap-3 px-4 py-3 text-left',
        'outline-none transition-colors hover:bg-surface-raised',
        'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border-focused',
        'disabled:pointer-events-none disabled:opacity-38',
        isExpired && 'text-content-error'
      )}
    >
      <UserAvatar
        firstName={account.firstName}
        lastName={account.lastName}
        variant={isCurrent ? 'current' : 'inactive'}
      />
      <span className="min-w-0 flex-1">
        <Text as="span" variant="body1" noWrap className="block font-medium">
          {account.firstName} {account.lastName}
        </Text>
        <Text
          as="span"
          variant="body2"
          color="secondary"
          noWrap
          className="block"
        >
          {account.email}
        </Text>
        {account.currentOrganisationName && (
          <Text
            as="span"
            variant="caption"
            color="secondary"
            noWrap
            className="block"
          >
            {account.currentOrganisationName}
          </Text>
        )}
      </span>
    </button>
  );
};
